
import { computed, defineComponent, reactive, toRefs, onMounted } from 'vue'
import AdaptiveWindowLayout from '@/layouts/AdaptiveWindowLayout.vue'
import SearchInput from '@/components/base/controls/SearchInput.vue'
import store, { MutationName } from '@/store'
import { BringInTicketType, getBatteryTicketName, getRelocationTicketName, getRepairTicketName } from '@/helpers/bike'
import router from '@/router'
import { Device, RepairTicket, SearchResultItem } from '@/types/bike'
import { BikeTicketName, TicketStatusName } from '@/enums/bike'
import { DASH_STRING } from '@/constants/until'
import { ToastText } from '@/enums/toast'
import { getBringInDevices, setupBikeFilter } from '@/services/bikeService'
import { handleErrorToast } from '@/helpers/errorHandler'
import { BasePoint } from '@/apis/v2Api'
import { BikeFilter } from '@/constants/bike'
import { emitter } from '@/helpers/eventBusHelper'
import { MapEvent } from '@/enums/eventType'

export default defineComponent({
  name: 'Search',
  components: {
    AdaptiveWindowLayout,
    SearchInput,
  },
  setup() {
    const state = reactive({
      keyword: '',
      totalCount: 0,
      maxLength: 6,
      searchResult: [] as Array<SearchResultItem>,
      bringInDevices: [[], []] as Array<Array<Device>>,
      searchedBasePoint: null as BasePoint | null,
      didSearch: false,
    })

    const fetchDevices = async () => {
      try {
        const devices = await getBringInDevices({ regionId: store.state.currentRegion.id })
        state.bringInDevices = devices
      } catch (e: any) {
        handleErrorToast(e)
      }
    }

    const devices = computed(() => store.state.devices)
    const goMainViewTicket = (item: SearchResultItem) => {
      store.commit(MutationName.SEARCHED_DEVICE_CODE, item.code)

      if ([
        BikeTicketName.REPAIR_FIELD,
        BikeTicketName.REPAIR_WAREHOUSE,
      ].includes(item.type)) {
        router.push({
          path: '/',
          query: {
            type: 'repair',
          }
        })
      } else if ([
        BikeTicketName.RELOCATION_1_5,
        BikeTicketName.RELOCATION_3,
        BikeTicketName.RELOCATION_5,
        BikeTicketName.RELOCATION_10,
      ].includes(item.type)) {
        router.push({
          path: '/',
          query: {
            type: 'relocation',
          }
        })
      } else if ([
        BikeTicketName.BATTERY,
      ].includes(item.type)) {
        router.push({
          path: '/',
          query: {
            type: 'battery',
          }
        })
      }

      emitter.emit(MapEvent.FOCUS_MARKER)
    }

    const goBringInView = (type: BringInTicketType) => {
      if (type === BringInTicketType.REPAIR) {
        router.push({
          path: '/bring-in'
        })
      } else {
        router.push({
          path: '/bring-in',
          query: {
            tab: 1
          }
        })
      }
    }

    const searchKeyword = (): void => {
      if (state.keyword.length < state.maxLength) {
        return store.commit(MutationName.SET_TOAST, {
          message: `번호 ${state.maxLength}자리로 입력해주세요.`,
          show: true,
        })
      }

      state.searchResult.splice(0, state.searchResult.length)
      state.searchedBasePoint = null
      state.didSearch = true

      const getComment = (ticket: RepairTicket) => {
        if (!ticket.comments || ticket.comments.length === 0) {
          return ''
        }
        const etc = ticket.comments.length > 1 ? `외 ${ticket.comments.length - 1}` : ''
        return `${ticket.comments[0].comment} ${etc}`
      }

      devices.value
        .filter((device: Device) => device.code === state.keyword.toLowerCase())
        .forEach((device: Device) => {
          if (Object.prototype.hasOwnProperty.call(device, TicketStatusName.RELOCATION_TICKET)) {
            state.searchResult.push({
              ...device,
              type: getRelocationTicketName(device.filter) || BikeTicketName.DEFAULT,
              code: device.code.toUpperCase(),
              comments: DASH_STRING,
            })
          }

          if (Object.prototype.hasOwnProperty.call(device, TicketStatusName.REPAIR_TICKET)) {
            state.searchResult.push({
              ...device,
              type: getRepairTicketName(device.filter) || BikeTicketName.DEFAULT,
              code: device.code.toUpperCase(),
              comments: device.repairTicket ? getComment(device.repairTicket) : DASH_STRING,
            })
          }

          if (Object.prototype.hasOwnProperty.call(device, TicketStatusName.BATTERY_TICKET)) {
            state.searchResult.push({
              ...device,
              type: getBatteryTicketName(),
              code: device.code.toUpperCase(),
              comments: DASH_STRING,
            })
          }
        })

      state.bringInDevices[0]
        .filter(device => device.code.toLowerCase() === state.keyword.toLowerCase())
        .forEach((device: Device) => {
          // device.filter = setupBikeFilter(device)
          setupBikeFilter(device)
          if (device.repairTicket) {
            state.searchedBasePoint = store.state[MutationName.BASE_POINT_MAP][device.repairTicket.base_point_id]
            state.searchResult.push({
              ...device,
              type: getRepairTicketName(device.filter) || BikeTicketName.DEFAULT,
              code: device.code.toUpperCase(),
              comments: device.repairTicket ? getComment(device.repairTicket) : DASH_STRING,
              isBringInTicket: true,
            })
          }
        })

      state.bringInDevices[1]
        .filter(device => device.code.toLowerCase() === state.keyword.toLowerCase())
        .forEach((device: Device) => {
          // device.filter = setupBikeFilter(device)
          setupBikeFilter(device)
          if (device.relocationTicket) {
            state.searchedBasePoint = state.searchedBasePoint || store.state[MutationName.BASE_POINT_MAP][device.relocationTicket.base_point_id]
            state.searchResult.push({
              ...device,
              type: getRelocationTicketName(device.filter) || BikeTicketName.DEFAULT,
              code: device.code.toUpperCase(),
              comments: DASH_STRING,
              isBringInTicket: true,
            })
          }
        })

      state.totalCount = state.searchResult.length

      if (state.searchResult.length === 0) {
        store.commit(MutationName.SET_TOAST, {
          message: ToastText.INVALID_CODE,
          show: true,
        })
      }
    }

    onMounted(() => {
      fetchDevices()
    })

    return {
      ...toRefs(state),
      searchKeyword,
      goMainViewTicket,
      store,
      MutationName,
      goBringInView,
      BringInTicketType,
      BikeFilter,
    }
  },
})
